import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { partsAPI, vehiclesAPI } from "@/lib/api";
import { HeaderIcon } from "@/lib/assets";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  Package,
  Plus,
  Search,
  Edit,
  Trash2,
  DollarSign,
  MapPin,
  Minus,
  Truck,
  Filter,
} from "lucide-react";

const INITIAL_FORM = {
  name: "",
  part_number: "",
  brand: "",
  quantity: 1,
  location: "Garage",
  cost_per_unit: "",
  supplier: "",
  notes: "",
  vehicle_id: "",
};

export default function PartsInventory() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [parts, setParts] = useState([]);
  const [stats, setStats] = useState({ unique_items: 0, total_parts: 0, total_value: 0 });
  const [vehicles, setVehicles] = useState([]);
  const [locations, setLocations] = useState([]);
  
  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [filterLocation, setFilterLocation] = useState("all");
  const [filterVehicle, setFilterVehicle] = useState("all");
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingPart, setEditingPart] = useState(null);
  const [form, setForm] = useState(INITIAL_FORM);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    loadParts();
  }, [filterLocation, filterVehicle]);

  const loadInitialData = async () => {
    try {
      const [vehiclesRes, locationsRes] = await Promise.all([
        vehiclesAPI.list(),
        partsAPI.locations(),
      ]);
      setVehicles(vehiclesRes.data.vehicles || []);
      setLocations(locationsRes.data || []);
      await loadParts();
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const loadParts = async () => {
    try {
      const params = {};
      if (filterLocation !== "all") params.location = filterLocation;
      if (filterVehicle !== "all") params.vehicle_id = filterVehicle;
      if (searchQuery) params.search = searchQuery;
      
      const { data } = await partsAPI.list(params);
      setParts(data.parts || []);
      setStats(data.stats || { unique_items: 0, total_parts: 0, total_value: 0 });
    } catch (error) {
      toast.error("Failed to load parts");
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    loadParts();
  };

  const openAddModal = () => {
    setEditingPart(null);
    setForm(INITIAL_FORM);
    setModalOpen(true);
  };

  const openEditModal = (part) => {
    setEditingPart(part);
    setForm({
      name: part.name || "",
      part_number: part.part_number || "",
      brand: part.brand || "",
      quantity: part.quantity || 1,
      location: part.location || "Garage",
      cost_per_unit: part.cost_per_unit || "",
      supplier: part.supplier || "",
      notes: part.notes || "",
      vehicle_id: part.vehicle_id || "",
    });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Part name is required");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: parseInt(form.quantity) || 1,
        cost_per_unit: parseFloat(form.cost_per_unit) || 0,
        vehicle_id: form.vehicle_id || null,
      };

      if (editingPart) {
        await partsAPI.update(editingPart.id, payload);
        toast.success("Part updated");
      } else {
        await partsAPI.create(payload);
        toast.success("Part added");
      }
      
      setModalOpen(false);
      loadParts();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save part");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (partId) => {
    if (!confirm("Delete this part?")) return;
    
    try {
      await partsAPI.delete(partId);
      toast.success("Part deleted");
      loadParts();
    } catch (error) {
      toast.error("Failed to delete part");
    }
  };

  const handleAdjustQuantity = async (partId, adjustment) => {
    try {
      await partsAPI.adjustQuantity(partId, adjustment);
      loadParts();
    } catch (error) {
      toast.error("Failed to update quantity");
    }
  };

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card/50 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Link
                to="/"
                data-testid="back-btn"
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </Link>
              <div className="flex items-center gap-3">
                <HeaderIcon />

                <h1 className="text-lg font-bold text-foreground">Parts Inventory</h1>
              </div>
            </div>
            <Button
              onClick={openAddModal}
              data-testid="add-part-btn"
              className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Part
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats */}
        <div className="grid gap-4 grid-cols-3 mb-6">
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-sm flex items-center justify-center">
                  <Package className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Items</p>
                  <p className="text-2xl font-mono font-bold text-foreground">{stats.unique_items}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-500/10 rounded-sm flex items-center justify-center">
                  <Package className="w-5 h-5 text-blue-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total Qty</p>
                  <p className="text-2xl font-mono font-bold text-foreground">{stats.total_parts}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500/10 rounded-sm flex items-center justify-center">
                  <DollarSign className="w-5 h-5 text-green-500" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground uppercase">Total Value</p>
                  <p className="text-2xl font-mono font-bold text-foreground">
                    ${stats.total_value.toLocaleString()}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="card-tactical bg-zinc-950/50 border-zinc-800 mb-6">
          <CardContent className="p-4">
            <div className="flex flex-wrap items-center gap-4">
              <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search parts..."
                    className="pl-10 bg-zinc-900 border-zinc-800"
                    data-testid="search-input"
                  />
                </div>
              </form>
              <Select value={filterLocation} onValueChange={setFilterLocation}>
                <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800" data-testid="location-filter">
                  <MapPin className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Location" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="all">All Locations</SelectItem>
                  {locations.map((loc) => (
                    <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filterVehicle} onValueChange={setFilterVehicle}>
                <SelectTrigger className="w-40 bg-zinc-900 border-zinc-800" data-testid="vehicle-filter">
                  <Truck className="w-4 h-4 mr-2 text-muted-foreground" />
                  <SelectValue placeholder="Vehicle" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-950 border-zinc-800">
                  <SelectItem value="all">All Vehicles</SelectItem>
                  {vehicles.map((v) => (
                    <SelectItem key={v.id} value={v.id}>{v.nickname}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Parts List */}
        {parts.length === 0 ? (
          <Card className="card-tactical bg-zinc-950/50 border-zinc-800">
            <CardContent className="p-12 text-center">
              <Package className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
              <h3 className="text-lg font-bold text-foreground mb-2">No Parts</h3>
              <p className="text-muted-foreground mb-6">
                Start tracking your parts inventory
              </p>
              <Button
                onClick={openAddModal}
                className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add First Part
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {parts.map((part) => (
              <Card key={part.id} className="card-tactical bg-zinc-950/50 border-zinc-800" data-testid={`part-${part.id}`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h3 className="font-bold text-foreground">{part.name}</h3>
                        {part.brand && (
                          <span className="text-sm text-muted-foreground">• {part.brand}</span>
                        )}
                      </div>
                      {part.part_number && (
                        <p className="text-sm text-muted-foreground font-mono">#{part.part_number}</p>
                      )}
                      <div className="flex items-center gap-4 mt-2 flex-wrap">
                        <span className="text-xs bg-zinc-800 px-2 py-1 rounded-sm flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {part.location}
                        </span>
                        {part.vehicle_nickname && (
                          <span className="text-xs bg-zinc-800 px-2 py-1 rounded-sm flex items-center gap-1">
                            <Truck className="w-3 h-3" />
                            {part.vehicle_nickname}
                          </span>
                        )}
                        {part.supplier && (
                          <span className="text-xs text-muted-foreground">
                            From: {part.supplier}
                          </span>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      {/* Quantity Controls */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 border-zinc-700"
                          onClick={() => handleAdjustQuantity(part.id, -1)}
                          disabled={part.quantity <= 0}
                        >
                          <Minus className="w-3 h-3" />
                        </Button>
                        <span className="w-12 text-center font-mono font-bold text-lg">
                          {part.quantity}
                        </span>
                        <Button
                          variant="outline"
                          size="icon"
                          className="h-8 w-8 border-zinc-700"
                          onClick={() => handleAdjustQuantity(part.id, 1)}
                        >
                          <Plus className="w-3 h-3" />
                        </Button>
                      </div>
                      
                      {/* Value */}
                      <div className="text-right min-w-[80px]">
                        <p className="text-sm font-mono text-green-500">
                          ${part.total_value.toLocaleString()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          ${part.cost_per_unit}/ea
                        </p>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEditModal(part)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-red-500 hover:text-red-400"
                          onClick={() => handleDelete(part.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Add/Edit Modal */}
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle>{editingPart ? "Edit Part" : "Add Part"}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Part Name *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => updateField("name", e.target.value)}
                placeholder="e.g., Oil Filter"
                className="bg-zinc-900 border-zinc-800"
                data-testid="part-name-input"
              />
            </div>
            
            <div className="grid gap-4 grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="brand">Brand</Label>
                <Input
                  id="brand"
                  value={form.brand}
                  onChange={(e) => updateField("brand", e.target.value)}
                  placeholder="e.g., K&N"
                  className="bg-zinc-900 border-zinc-800"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="part_number">Part Number</Label>
                <Input
                  id="part_number"
                  value={form.part_number}
                  onChange={(e) => updateField("part_number", e.target.value)}
                  placeholder="e.g., HP-1001"
                  className="bg-zinc-900 border-zinc-800 font-mono"
                />
              </div>
            </div>
            
            <div className="grid gap-4 grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="quantity">Quantity</Label>
                <Input
                  id="quantity"
                  type="number"
                  min="0"
                  value={form.quantity}
                  onChange={(e) => updateField("quantity", e.target.value)}
                  className="bg-zinc-900 border-zinc-800 font-mono"
                  data-testid="part-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cost_per_unit">Cost per Unit ($)</Label>
                <Input
                  id="cost_per_unit"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost_per_unit}
                  onChange={(e) => updateField("cost_per_unit", e.target.value)}
                  placeholder="0.00"
                  className="bg-zinc-900 border-zinc-800 font-mono"
                />
              </div>
            </div>
            
            <div className="grid gap-4 grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="location">Location</Label>
                <Select value={form.location} onValueChange={(v) => updateField("location", v)}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {locations.map((loc) => (
                      <SelectItem key={loc} value={loc}>{loc}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="vehicle">For Vehicle (optional)</Label>
                <Select value={form.vehicle_id || "none"} onValueChange={(v) => updateField("vehicle_id", v === "none" ? "" : v)}>
                  <SelectTrigger className="bg-zinc-900 border-zinc-800">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    <SelectItem value="none">None</SelectItem>
                    {vehicles.map((v) => (
                      <SelectItem key={v.id} value={v.id}>{v.nickname}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="supplier">Supplier</Label>
              <Input
                id="supplier"
                value={form.supplier}
                onChange={(e) => updateField("supplier", e.target.value)}
                placeholder="e.g., Amazon, AutoZone"
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Input
                id="notes"
                value={form.notes}
                onChange={(e) => updateField("notes", e.target.value)}
                placeholder="Optional notes..."
                className="bg-zinc-900 border-zinc-800"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setModalOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              data-testid="save-part-btn"
              className="btn-tactical bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingPart ? "Update" : "Add Part"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
